/*
Usage Notes:

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      currency='{{ cart.currency.iso_code }}'
      bulkVariantIds='{%- for v in product.variants -%}{%- if v.metafields.custom.variant_wide_picker_image -%}{{ v.id }},{%- endif -%}{%- endfor -%}'
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
  {% endif %}

  Note: formId not required if element is inside of a form already

  The owning form publishes theme:variant:change with { variant, formId }.
  product-pricing.js supplies Shopify allocation prices and per-form state.
*/

import './product-pricing.js';

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioStyles = css`
  :host {
    --skio-accent: #00555A;
    --skio-border-selected: #00555A;
    --skio-text: inherit;
    --skio-text-secondary: inherit;
    --skio-badge-text: var(--skio-text);
    --skio-radius: 8px;
  }

  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
    border: 0;
    max-width: 520px;
    margin-bottom: 20px;
    font-family: inherit;
  }

  .skio-flex {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .skio-onetime-second {
    order: 2;
  }

  /* --- Row containers --- */
  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    border: 1px solid var(--color-hex-gray);
    border-radius: var(--skio-radius);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    background: #fff;
  }
  .skio-group-container--subscription {
    cursor: pointer;
  }
  .skio-group-container--selected {
    border-color: var(--skio-border-selected);
    box-shadow: 0 0 0 1px var(--skio-border-selected);
  }

  /* --- Hidden radio input --- */
  .skio-group-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px var(--skio-accent) solid;
    outline-offset: 2px;
    border-radius: var(--skio-radius);
  }

  /* --- Label (clickable row) --- */
  .skio-group-label {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    cursor: pointer;
    padding: 14px 16px;
    gap: 12px;
    position: relative;
  }

  /* --- Topline (radio + left content + right price) --- */
  .skio-group-topline {
    display: flex;
    align-items: flex-start;
    width: 100%;
    font-size: 15px;
    gap: 10px;
  }

  /* --- Radio circle --- */
  .skio-radio__container {
    display: flex;
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--skio-accent);
  }

  .skio-radio {
    transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.5;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  /* --- Left content block (title + subcopy) --- */
  .skio-left {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .skio-group-title {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: baseline;
    font-size: 15px;
    font-weight: 500;
    color: var(--skio-text);
  }

  .skio-subcopy {
    font-size: 13px;
    color: var(--skio-text-secondary);
    line-height: 1.4;
    margin: 0;
  }

  .skio-subcopy-break {
    display: none;
  }

  @media screen and (max-width: 439px) {
    .skio-subcopy-sep {
      display: none;
    }
    .skio-subcopy-break {
      display: inline;
    }
  }

  /* --- Right price block --- */
  .skio-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    gap: 2px;
    text-align: right;
    min-width: 70px;
  }

  .skio-most-popular-badge,
  .skio-best-value-badge {
    display: inline-block;
    background: #e3e3e3;
    color: var(--skio-badge-text);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 3px 8px;
    border-radius: 4px;
    margin-top: 6px;
    width: fit-content;
    white-space: nowrap;
  }

  .skio-price {
    font-weight: 700;
    font-size: 18px;
    color: var(--skio-text);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
  }

  @media screen and (min-width: 500px) {
    .skio-price {
      font-size: 22px;
    }
  }

  /* Subscription right-side price with original "was" price strike-through */
  .skio-price.skio-price--with-was {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }

  .skio-was-price {
    font-weight: 400;
    font-size: 14px;
    color: var(--skio-text-secondary);
    opacity: 0.6;
  }

  @media screen and (min-width: 500px) {
    .skio-was-price {
      font-size: 16px;
    }
  }

  .bb-skio-custom-discount-display {
    font-weight: normal;
    font-size: 14px;
  }

  /* --- Frequency select (rendered inside selected subscription card) --- */
  .skio-frequency-wrap {
    padding: 0 16px 14px 48px;
  }

  .skio-select {
    font-weight: 600;
    font-size: 13px;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 10px;
    margin-top: 5px;
    border-radius: var(--skio-radius);
    width: 100%;
    border: 1px solid var(--color-hex-gray);
    font-size: 14px;
    font-family: inherit;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    max-width: 290px;
    color: var(--skio-text);
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }
  .skio-frequency span {
    text-transform: lowercase;
  }

  /* --- Bottom call-out bar (connected to subscription row) --- */
  .skio-group-container--subscription {
    border-radius: var(--skio-radius);
    overflow: hidden;
  }

  .skio-callout-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--skio-accent);
    color: #fff;
    height: 30px;
    padding: 0 16px;
    font-size: 12px;
    gap: 10px;
    border-radius: 0px 0px 6px 6px;
  }

  .skio-callout-bar--left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  .skio-callout-bar--left strong {
    font-weight: 700;
  }

  .skio-callout-bar--right {
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
  }

  /* --- Benefits list (always visible inside subscription card) --- */
  .skio-benefits {
    display: grid;
    gap: 6px;
    padding: 4px 16px 14px 48px;
  }

  .skio-benefits__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 1.3;
    color: var(--skio-text-secondary);
  }

  .skio-benefits__row svg {
    flex-shrink: 0;
    color: var(--skio-accent);
  }

`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the Skio plan picker
    
    formId: { type: String },             //optional; if passed, used to connect input fields to form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object

    selectedVariant: { type: Object },    //placeholder for data
    skioSellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data

    defaultFrequency: {},                 //placeholder for data

    bulkVariantIds: { type: String },     // optional; comma-separated list of variant IDs flagged as bulk/multi-pack (via the custom.variant_wide_picker_image metafield). Bulk variants show a "Best Value" badge instead of the per-day subcopy.
    variantDaysMap: { type: String },     // optional; comma-separated "variantId:days" pairs sourced from each variant's custom.days_standard_serving_size metafield. Used by perDayPrice() instead of the hardcoded 30-day fallback.
    variantPrefixMap: { type: String },   // optional; comma-separated "variantId:text" pairs sourced from each variant's custom.prefix_text_price_per_day_value metafield. Prepended before the per-day price in the one-time purchase subcopy, e.g. "From £0.58 per day".
    benefits: { type: String },           // optional; newline-separated override for the subscription benefits tick list. Each line becomes one green-tick row. When blank, falls back to the hardcoded subscriptionBenefits default.
    planFilter: { type: String, attribute: 'plan-filter' } // optional; 'flat' shows only plans with a single price adjustment, 'tiered' shows only plans with multiple adjustments (intro offers). Added for the BAR-22 Skio A/B test where one group holds both plan styles; remove when the test ends and Skio can split groups natively.
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.discountFormat = 'percent';

    this.currency = Shopify.currency.active;
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;

    this.bulkVariantIds = '';

    this.subscriptionBenefits = [
      '15% off, forever',
      'Free shipping (always on us)',
      'Skip, swap, or cancel whenever',
      'Earn reward points for even more off',
    ];

    // Optional per-instance override, passed via the `benefits` attribute (one benefit per line).
    this.benefits = '';

  }

  // Returns the override list when a `benefits` attribute was provided (one item per line),
  // otherwise the hardcoded subscriptionBenefits default.
  get benefitsList() {
    if (this.benefits && this.benefits.trim()) {
      return this.benefits.split('\n').map((s) => s.trim()).filter(Boolean);
    }
    return this.subscriptionBenefits;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    this.handleVariantChanged = (event) => {
      if (event.detail?.formId !== this.formId) return;
      const variantId = event.detail.variant?.id;
      if (this.selectedVariant?.id === variantId) return;
      this.selectedSellingPlan = null;
      this.selectedVariant = this.product?.variants.find((item) => item.id === variantId) ?? null;
      this.requestUpdate();
    };
    document.addEventListener('theme:variant:change', this.handleVariantChanged);

    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

  }

  disconnectedCallback() {
    document.removeEventListener('theme:variant:change', this.handleVariantChanged);
    super.disconnectedCallback();
  }

  render() {
    if(!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0 || !this.product?.available) return;
    
    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        <div class="skio-flex">

          <!-- ONE-TIME ROW -->
          <div class="skio-group-container ${ this.product.requires_selling_plan == false ? 'skio-group-container--available' : '' } ${ this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : '' } ${ this.subscriptionFirst ? 'skio-onetime-second' : ''}" skio-group-container skio-group-container--onetime
            @click=${() => { this.trackSelection(null); this.selectSellingPlanGroup(null); } } >

            <input id="skio-one-time-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }" type="radio" value=""
              skio-one-time ?checked=${ this.startSubscription == false && this.product.requires_selling_plan == false ? true : false }>
            <label class="skio-group-label" for="skio-one-time-${ this.key }">
              <div class="skio-group-topline">
                <div class="skio-radio__container">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                    <circle class="skio-radio" cx="12" cy="12" r="9" fill="currentColor"></circle>
                  </svg>
                </div>
                <div class="skio-left">
                  <div class="skio-group-title">
                    One-time purchase
                  </div>
                  ${ this.isBulkVariant() ? html`
                    <span class="skio-best-value-badge">Best Value</span>
                  ` : html`
                    <p class="skio-subcopy">${ this.shouldShowPerDayPrice() ? html`${this.getVariantPrefix() ? html`${this.getVariantPrefix()} ` : html``}${this.perDayPrice(this.offer().price)} per day<span class="skio-subcopy-sep"> - </span><br class="skio-subcopy-break">` : html`` }Free delivery on all orders</p>
                  ` }
                </div>
                <div class="skio-right">
                  <div class="skio-price ${ this.offer().savings > 0 ? 'skio-price--with-was' : '' }">
                    ${ this.offer().savings > 0 ? html`
                      <span class="bb-skio-custom-discount-display"><s>${this.money(this.offer().compareAtPrice)}</s></span>
                    ` : html`` }
                    <span skio-onetime-price>${ this.money(this.offer().price) }</span>
                  </div>
                </div>
              </div>
            </label>
          </div>

          <!-- SUBSCRIPTION ROWS -->
          ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) =>
            html`
              <div class="skio-group-container skio-group-container--available skio-group-container--subscription ${ this.selectedSellingPlanGroup == group ? 'skio-group-container--selected' : '' }" skio-group-container
                @click=${() => { this.trackSelection(group); this.selectSellingPlanGroup(group); } }>
                <input id="skio-selling-plan-group-${ index }-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }"
                  type="radio" value="${ group.id }" skio-selling-plan-group="${ group.id }" ?checked=${ this.selectedSellingPlanGroup == group ? true : false } >
                <label class="skio-group-label" for="skio-selling-plan-group-${ index }-${ this.key }">
                  <div class="skio-group-topline">
                    <div class="skio-radio__container">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                        <circle class="skio-radio" cx="12" cy="12" r="9" fill="currentColor"></circle>
                      </svg>
                    </div>
                    <div class="skio-left">
                      <div class="skio-group-title">
                        ${ group.name == 'Subscription' ? 'Subscribe & Save' : group.name }
                        ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                          ${ this.discountFormat == 'percent' ? this.discount(group.selected_selling_plan).percent : this.money(this.discount(group.selected_selling_plan).amount) }
                        ` : html`` }
                      </div>
                      <span class="skio-most-popular-badge">Most Popular</span>
                    </div>
                    <div class="skio-right">
                      <div class="skio-price skio-price--with-was">
                        ${ this.discount(group.selected_selling_plan).amount > 0 ? html`
                          <span class="skio-was-price"><s>${this.money(this.offer(group.selected_selling_plan).compareAtPrice)}</s></span>
                        ` : html`` }
                        <span skio-subscription-price>${ this.price(group.selected_selling_plan) }</span>
                      </div>
                    </div>
                  </div>
                </label>
                <div class="skio-benefits">
                  ${ this.benefitsList.map((benefit) => html`
                    <div class="skio-benefits__row">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022"/>
                      </svg>
                      <span>${benefit}</span>
                    </div>
                  `) }
                </div>
                ${ this.selectedSellingPlanGroup == group ? html`
                  <div class="skio-frequency-wrap">
                    <div class="skio-select">
                      Deliver Every:<br>
                      <select skio-selling-plans="${ group.id }" class="skio-frequency${ group.selling_plans.length == 1 ? ' skio-frequency--one' : '' }"
                        @change=${ (e) => this.selectSellingPlan(e.target, group) }>
                        ${ group.selling_plans.map((selling_plan) =>
                          html`
                          <option value="${ selling_plan.id }" ?selected=${group.selected_selling_plan == selling_plan }>
                            ${ selling_plan.name.replaceAll('Every ', '') }
                          </option>
                          `
                        ) }
                      </select>
                    </div>
                  </div>
                ` : html``}
                ${ this.shouldShowPerDayPrice() ? html`
                  <div class="skio-callout-bar">
                    <div class="skio-callout-bar--left">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                      </svg>
                      <span>That's just <strong>${this.perDayPrice(this.offer(group.selected_selling_plan).perDeliveryPrice)} a day</strong></span>
                    </div>
                    ${ this.discount(group.selected_selling_plan).amount > 0 ? html`
                      <div class="skio-callout-bar--right">Save ${this.money(this.discount(group.selected_selling_plan).amount)}</div>
                    ` : html`` }
                  </div>
                ` : html`` }
              </div>
            `
          ): ''}
        </div>

      </fieldset>`
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO'
      )

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function(a,b){
          if (parseInt(a.name.replace(/\D/g, "")) < parseInt(b.name.replace(/\D/g, ""))) return -1;
          if (parseInt(a.name.replace(/\D/g, "")) > parseInt(b.name.replace(/\D/g, ""))) return 1;
          if (parseInt(a.name.replace(/\D/g, "")) == parseInt(b.name.replace(/\D/g, ""))) return 0;
        })
      });

    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      let skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups));
      this.availableSellingPlanGroups = skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter(x => this.selectedVariant.selling_plan_allocations.find(y => y.selling_plan_id == x.id) );
        })

        // BAR-22: filter plans by shape when `plan-filter` is set (one Skio group holds both flat and tiered intro offers).
        if (this.planFilter) {
          const wantTiered = this.planFilter === 'tiered';
          this.availableSellingPlanGroups.forEach((group) => {
            group.selling_plans = group.selling_plans.filter(p => (p.price_adjustments?.length > 1) === wantTiered);
          });
          this.availableSellingPlanGroups = this.availableSellingPlanGroups.filter(g => g.selling_plans.length > 0);
        }

        this.availableSellingPlanGroups.forEach((group => {
          if (this.defaultFrequency) {
            let selling_plan = group.selling_plans.find(x => x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()));
            if (selling_plan) group.selected_selling_plan = selling_plan;
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
       }));

        if (this.startSubscription == true || this.product.requires_selling_plan == true || this.purchaseOption == 'subscription') {
          //find a matching selling plan, or choose first available

          if (this.selectedSellingPlan == null || this.selectedSellingPlan == undefined) {
            this.selectSellingPlanGroup(this.availableSellingPlanGroups[0]);
          }

          let sellingPlanName = this.selectedSellingPlan?.name;

          let sellingPlanGroup = this.availableSellingPlanGroups.find(x => x.selling_plans.find(y => y.name == sellingPlanName));
          let sellingPlan = sellingPlanGroup?.selling_plans.find(y => y.name == sellingPlanName);
          if (sellingPlan) {
            this.selectedSellingPlanGroup = sellingPlanGroup;
            this.selectedSellingPlan = sellingPlan;
          } else {
            this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
            this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0];
          }

        } else {
          this.selectedSellingPlan = null;
          this.selectedSellingPlanGroup = null;
        }
      } else {
        this.selectedSellingPlan = null;
        this.selectedSellingPlanGroup = null;
      }

      //update the form that was passed, if any
      this.updateForm();

    }

    if(changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();

    }

    if(changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }

  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  trackSelection(group) {
    const isSubscription = group != null;
    const wasSubscription = this.selectedSellingPlanGroup != null;
    if (isSubscription === wasSubscription) return;

    window.sendEvent?.(isSubscription ? 'PDP - Subscribe Selection' : 'PDP - OTP Selection', {
      event_detail: this.product?.title,
    });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (group) this.purchaseOption = 'subscription'
    else this.purchaseOption = 'onetime'

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Shared allocation lookup also drives the theme's title/sticky displays.
  offer(sellingPlan = null) {
    const form = document.getElementById(this.formId);
    const pricing = form && window.BareProductPricing.forForm(form);
    if (pricing?.state?.variantId === this.selectedVariant?.id) {
      return pricing.offer(sellingPlan?.id);
    }
    // Initial render can precede the owning form/controller (standalone block).
    return window.BareProductPricing.offer(this.selectedVariant, sellingPlan?.id);
  }

  discount(sellingPlan) {
    if (!sellingPlan) return { percent: '0%', amount: 0 };
    const offer = this.offer(sellingPlan);
    return { percent: `${offer.savingsPercent}%`, amount: offer.savings };
  }

  price(sellingPlan, formatted = true) {
    const offer = this.offer(sellingPlan);
    return formatted ? this.money(offer.price) : offer.price;
  }

  // Whether the currently selected variant is a bulk/multi-pack variant.
  // Bulk variants are flagged per-variant via the custom.variant_wide_picker_image
  // metafield; Liquid collects their IDs into the bulkVariantIds attribute. As a
  // fallback we also treat any variant with "bulk" in its title as bulk.
  isBulkVariant() {
    if (!this.selectedVariant) return false;
    if (this.bulkVariantIds) {
      const ids = this.bulkVariantIds.split(',').map(id => id.trim()).filter(Boolean);
      if (ids.includes(String(this.selectedVariant.id))) return true;
    }
    if (this.selectedVariant.title.toLowerCase().includes('bulk')) return true;
    return false;
  }

  // Determines if per-day pricing should be shown for this product/variant
  shouldShowPerDayPrice() {
    // Not for bulk/multi-pack variants
    if (this.isBulkVariant()) return false;

    return true;
  }

  // Looks up the standard serving size (in days) for the currently selected variant from
  // variantDaysMap (built in Liquid from each variant's custom.days_standard_serving_size
  // metafield). Returns null if no value is set for this variant.
  getVariantDays() {
    if (!this.variantDaysMap || !this.selectedVariant) return null;

    const entry = this.variantDaysMap
      .split(',')
      .map(pair => pair.trim())
      .filter(Boolean)
      .find(pair => pair.split(':')[0] === String(this.selectedVariant.id));

    if (!entry) return null;

    const days = parseInt(entry.split(':')[1], 10);
    return Number.isFinite(days) && days > 0 ? days : null;
  }

  // Looks up the "From"-style prefix text for the currently selected variant from
  // variantPrefixMap (built in Liquid from each variant's custom.prefix_text_price_per_day_value
  // metafield). Returns null if no value is set for this variant.
  getVariantPrefix() {
    if (!this.variantPrefixMap || !this.selectedVariant) return null;

    const entry = this.variantPrefixMap
      .split(',')
      .map(pair => pair.trim())
      .filter(Boolean)
      .find(pair => pair.split(':')[0] === String(this.selectedVariant.id));

    if (!entry) return null;

    const text = entry.slice(entry.indexOf(':') + 1).trim();
    return text.length > 0 ? text : null;
  }

  // Calculates and formats the per-day price, rounded up to nearest penny.
  // Prefers the variant's own standard serving size (custom.days_standard_serving_size
  // metafield, via variantDaysMap); falls back to the old 30-day assumption when that
  // metafield isn't set on the variant.
  perDayPrice(priceInCents) {
    const variantDays = this.getVariantDays();
    const days = variantDays !== null ? variantDays : 30;

    const perDay = priceInCents / 100 / days;
    const rounded = Math.ceil(perDay * 100) / 100;
    return this.moneyFormatter.format(rounded);
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.getElementById(this.formId);
      let selling_plan_input = this.nextElementSibling?.matches('[data-skio-selling-plan-input]')
        ? this.nextElementSibling
        : null;
      let discount_input = selling_plan_input?.nextElementSibling?.matches('[data-skio-discount-input]')
        ? selling_plan_input.nextElementSibling
        : null;

      // The featured-product picker can render before its form. Its light-DOM
      // inputs use the HTML form attribute, so update them even before the form
      // itself has been parsed.
      if (selling_plan_input || form) {
        selling_plan_input = selling_plan_input || form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement('input');
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        let discountValue = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
        if (discountValue == '0%') discountValue = null;
        if (discountValue !== null && this.discountFormat !== 'percent') discountValue = this.money(this.discount(this.selectedSellingPlan).amount);

        discount_input = discount_input || form?.querySelector('[name="properties[Discount]"]');
        if (discount_input) {
          discount_input.value = discountValue;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
        } else if (form) {
          discount_input = document.createElement('input');
          discount_input.type = "hidden";
          discount_input.name = "properties[Discount]";
          discount_input.value = discountValue;
          discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          if (discountValue == null) discount_input.disabled = true;
          form.append(discount_input);
        }

        if (form) {
          const selectionKey = `${this.selectedVariant?.id}:${selling_plan_input?.disabled ? '' : selling_plan_input?.value}`;
          if (selectionKey !== this.lastNotifiedSelection) {
            this.lastNotifiedSelection = selectionKey;
            form.dispatchEvent(new CustomEvent('skio:selection:change', { bubbles: true }));
          }
        }
      }
    }
  }

}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);
